import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { apiClient } from '@/api/client';
import { useToast } from '@/hooks/use-toast';
import type {
  RecurringJobSchedule,
  RecurringJobScheduleSummary,
  RecurringJobScheduleInput,
  RecurringJobScheduleUpdate,
} from '@jobos/shared';

interface SchedulesResponse {
  schedules: RecurringJobScheduleSummary[];
}
interface ScheduleResponse {
  schedule: RecurringJobScheduleSummary;
}
interface ScheduleByJobResponse {
  schedule: RecurringJobSchedule | null;
}

export const recurringScheduleKeys = {
  all: ['recurring-schedules'] as const,
  lists: () => [...recurringScheduleKeys.all, 'list'] as const,
  due: () => [...recurringScheduleKeys.all, 'due'] as const,
  byJob: (jobId: string) => [...recurringScheduleKeys.all, 'by-job', jobId] as const,
};

export function useRecurringSchedules() {
  return useQuery({
    queryKey: recurringScheduleKeys.lists(),
    queryFn: async () => {
      const res = await apiClient.get<SchedulesResponse>('/recurring-schedules');
      return res.schedules;
    },
  });
}

export function useDueRecurringSchedules() {
  return useQuery({
    queryKey: recurringScheduleKeys.due(),
    queryFn: async () => {
      const res = await apiClient.get<SchedulesResponse>('/recurring-schedules/due');
      return res.schedules;
    },
    refetchOnWindowFocus: true,
    staleTime: 60 * 1000,
  });
}

export function useRecurringScheduleByJob(jobId: string | undefined) {
  return useQuery({
    queryKey: recurringScheduleKeys.byJob(jobId ?? ''),
    queryFn: async () => {
      const res = await apiClient.get<ScheduleByJobResponse>(`/recurring-schedules/by-job/${jobId}`);
      return res.schedule;
    },
    enabled: !!jobId,
  });
}

function invalidateAll(queryClient: ReturnType<typeof useQueryClient>) {
  queryClient.invalidateQueries({ queryKey: recurringScheduleKeys.all });
}

export function useCreateRecurringSchedule() {
  const queryClient = useQueryClient();
  const { toast } = useToast();
  return useMutation({
    mutationFn: async (input: RecurringJobScheduleInput) => {
      const res = await apiClient.post<ScheduleResponse>('/recurring-schedules', input);
      return res.schedule;
    },
    onSuccess: () => {
      invalidateAll(queryClient);
      toast({ title: 'Recurring reminder set up' });
    },
    onError: (error: Error) => {
      toast({ title: 'Error', description: error.message, variant: 'destructive' });
    },
  });
}

export function useUpdateRecurringSchedule() {
  const queryClient = useQueryClient();
  const { toast } = useToast();
  return useMutation({
    mutationFn: async ({ id, update }: { id: string; update: RecurringJobScheduleUpdate }) => {
      const res = await apiClient.patch<ScheduleResponse>(`/recurring-schedules/${id}`, update);
      return res.schedule;
    },
    onSuccess: () => {
      invalidateAll(queryClient);
      toast({ title: 'Recurring reminder updated' });
    },
    onError: (error: Error) => {
      toast({ title: 'Error', description: error.message, variant: 'destructive' });
    },
  });
}

export function useDeleteRecurringSchedule() {
  const queryClient = useQueryClient();
  const { toast } = useToast();
  return useMutation({
    mutationFn: async (id: string) => {
      await apiClient.delete(`/recurring-schedules/${id}`);
    },
    onSuccess: () => {
      invalidateAll(queryClient);
      toast({ title: 'Recurring reminder removed' });
    },
    onError: (error: Error) => {
      toast({ title: 'Error', description: error.message, variant: 'destructive' });
    },
  });
}

export function useAcknowledgeRecurringSchedule() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async (id: string) => {
      const res = await apiClient.post<ScheduleResponse>(`/recurring-schedules/${id}/acknowledge`);
      return res.schedule;
    },
    onSuccess: () => {
      invalidateAll(queryClient);
    },
  });
}
